// Capital minimap + full map, with FOG OF WAR.
//
//  • Mini map (bottom-right): a local, rotating view centred on the hero — "up" is always up on screen.
//  • Full map (M or the ⤢ button): the whole city, north-up, with discovered landmarks named.
//  • Fog of war: the city starts dark and clears as the hero walks around (15 m radius). What you've
//    explored is remembered between visits (localStorage). Landmarks only appear once discovered.
//
// Everything on the map is real: positions come from the hero, the wandering townsfolk and the landmark file.
// Map picture: public/models/capital/capital_map.jpg (baked from the city art — see scripts/capital/bake_map.py).

import { Events, emit } from './eventBus.js'

const STORE = 'mao_capital_fog_v1'
const CELL_M = 2          // fog resolution: one fog cell = 2 m
const REVEAL_R = 15       // metres the hero "sees"
const FOUND_R = 9         // metres to discover a landmark
const MINI_PX_PER_M = 3.2 // mini map zoom (≈ 70 m across)
const GLYPH = { market: '市', gate: '門', palace: '朝', bridge: '橋', temple: '廟', shrine: '祠', pond: '池', torii: '坊' }

export function createCapitalMap() {
  const root = document.createElement('aside')
  root.className = 'cap-map'
  root.setAttribute('aria-label', 'Capital map')
  root.innerHTML = `
    <div class="cap-map__head"><span>CAPITAL <i>京城</i></span><button type="button" class="cap-map__expand" title="Open the full map (M)" aria-label="Open the full map">⤢</button></div>
    <canvas class="cap-map__cv"></canvas>
    <div class="cap-map__near" data-near></div>`
  document.body.appendChild(root)
  const cv = root.querySelector('canvas')
  const ctx = cv.getContext('2d')
  const nearEl = root.querySelector('[data-near]')

  const bigEl = document.createElement('div')
  bigEl.className = 'cap-bigmap'
  bigEl.innerHTML = `
    <div class="cap-bigmap__card" role="dialog" aria-label="Capital map">
      <div class="cap-bigmap__head"><span>MAP OF THE CAPITAL <i>京城</i></span><span data-pct></span><button type="button" class="cap-bigmap__x" aria-label="Close map">×</button></div>
      <canvas class="cap-bigmap__cv"></canvas>
      <ul class="cap-bigmap__legend" data-legend></ul>
    </div>`
  document.body.appendChild(bigEl)
  const bcv = bigEl.querySelector('canvas')
  const bctx = bcv.getContext('2d')

  let img = null, data = null, meta = null
  let gw = 0, gh = 0, grid = null, reachable = null, reachableCount = 0
  let fog = null, fogCtx = null, fogDirty = true
  const discovered = new Set()
  let visible = false, bigOpen = false, frame = 0
  let lastRevealX = NaN, lastRevealZ = NaN, saveTimer = null
  let state = { hx: 0, hz: 0, heading: 0, az: 0, npcs: [] }

  // ── persistence ──────────────────────────────────────────────────────────
  const save = () => {
    clearTimeout(saveTimer)
    saveTimer = setTimeout(() => {
      try {
        let bin = ''
        for (let i = 0; i < grid.length; i++) bin += String.fromCharCode(grid[i])
        localStorage.setItem(STORE, JSON.stringify({ gw, gh, g: btoa(bin), d: [...discovered] }))
      } catch { /* private mode / quota */ }
    }, 2500)
  }
  const load = () => {
    try {
      const j = JSON.parse(localStorage.getItem(STORE) || 'null')
      if (!j || j.gw !== gw || j.gh !== gh) return
      const bin = atob(j.g)
      for (let i = 0; i < grid.length && i < bin.length; i++) grid[i] = bin.charCodeAt(i)
      for (const id of j.d ?? []) discovered.add(id)
    } catch { /* ignore */ }
  }

  // ── set-up once the assets are ready ───────────────────────────────────────
  function attach({ image, pois, nav }) {
    img = image
    data = pois
    meta = pois.map
    gw = Math.ceil(meta.w / CELL_M)
    gh = Math.ceil(meta.h / CELL_M)
    grid = new Uint8Array(gw * gh)
    // Which fog cells contain somewhere the hero can actually stand (so "explored %" is honest).
    reachable = new Uint8Array(gw * gh)
    if (nav) {
      for (let cz = 0; cz < nav.nz; cz++) {
        for (let cx = 0; cx < nav.nx; cx++) {
          const i = cz * nav.nx + cx
          if (nav.start[i + 1] === nav.start[i]) continue
          const fx = Math.floor((nav.x0 + (cx + 0.5) * nav.cell - meta.x0) / CELL_M)
          const fz = Math.floor((nav.z0 + (cz + 0.5) * nav.cell - meta.z0) / CELL_M)
          if (fx >= 0 && fz >= 0 && fx < gw && fz < gh) reachable[fz * gw + fx] = 1
        }
      }
      for (let i = 0; i < reachable.length; i++) reachableCount += reachable[i]
    }
    fog = document.createElement('canvas')
    fog.width = gw * 4
    fog.height = gh * 4
    fogCtx = fog.getContext('2d')
    load()
    fogDirty = true
    sizeCanvases()
  }

  function sizeCanvases() {
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const cssW = 224
    const cssH = 200
    cv.style.width = `${cssW}px`
    cv.style.height = `${cssH}px`
    cv.width = Math.round(cssW * dpr)
    cv.height = Math.round(cssH * dpr)
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx._w = cssW
    ctx._h = cssH
    const bw = Math.min(window.innerWidth * 0.86, 760)
    const bh = Math.min(window.innerHeight * 0.66, bw * (meta ? meta.h / meta.w : 0.92))
    const bwFit = meta ? Math.min(bw, bh * (meta.w / meta.h)) : bw
    bcv.style.width = `${bwFit}px`
    bcv.style.height = `${meta ? bwFit * (meta.h / meta.w) : bh}px`
    bcv.width = Math.round(bwFit * dpr)
    bcv.height = Math.round((meta ? bwFit * (meta.h / meta.w) : bh) * dpr)
    bctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    bctx._w = bwFit
    bctx._h = meta ? bwFit * (meta.h / meta.w) : bh
  }
  window.addEventListener('resize', () => { if (img) sizeCanvases() })

  // ── fog ──────────────────────────────────────────────────────────────────
  function rebuildFog() {
    fogDirty = false
    const W = fog.width
    const H = fog.height
    fogCtx.globalCompositeOperation = 'source-over'
    fogCtx.clearRect(0, 0, W, H)
    fogCtx.fillStyle = 'rgba(14, 16, 12, 0.95)'
    fogCtx.fillRect(0, 0, W, H)
    // Cut a soft hole for every explored cell.
    const tmp = rebuildFog._tmp ?? (rebuildFog._tmp = document.createElement('canvas'))
    tmp.width = W
    tmp.height = H
    const t = tmp.getContext('2d')
    t.fillStyle = '#000'
    for (let z = 0; z < gh; z++) for (let x = 0; x < gw; x++) if (grid[z * gw + x]) t.fillRect(x * 4 - 2, z * 4 - 2, 8, 8)
    fogCtx.globalCompositeOperation = 'destination-out'
    fogCtx.filter = 'blur(5px)'
    fogCtx.drawImage(tmp, 0, 0)
    fogCtx.filter = 'none'
    fogCtx.globalCompositeOperation = 'source-over'
  }

  function reveal(hx, hz) {
    if (Math.hypot(hx - lastRevealX, hz - lastRevealZ) < 1) return
    lastRevealX = hx
    lastRevealZ = hz
    const cx = (hx - meta.x0) / CELL_M
    const cz = (hz - meta.z0) / CELL_M
    const r = REVEAL_R / CELL_M
    let changed = false
    for (let z = Math.max(0, Math.floor(cz - r)); z <= Math.min(gh - 1, Math.ceil(cz + r)); z++) {
      for (let x = Math.max(0, Math.floor(cx - r)); x <= Math.min(gw - 1, Math.ceil(cx + r)); x++) {
        if (Math.hypot(x + 0.5 - cx, z + 0.5 - cz) <= r && !grid[z * gw + x]) { grid[z * gw + x] = 1; changed = true }
      }
    }
    if (changed) { fogDirty = true; save() }
    for (const p of data.pois) {
      if (!discovered.has(p.id) && Math.hypot(hx - p.x, hz - p.z) <= FOUND_R) {
        discovered.add(p.id)
        save()
        emit(Events.CapitalPoiFound, { poi: p })
      }
    }
  }

  const explored = () => {
    if (!grid) return 0
    let n = 0
    for (let i = 0; i < grid.length; i++) if (grid[i] && reachable[i]) n++
    return reachableCount ? Math.round((100 * n) / reachableCount) : 0
  }

  // ── drawing ──────────────────────────────────────────────────────────────
  const mapPx = (x, z) => [(x - meta.x0) * meta.ppm, (z - meta.z0) * meta.ppm]

  function drawIcon(c, sx, sy, kind, size = 7, ring = '#f0e2b8') {
    c.beginPath(); c.arc(sx, sy, size, 0, Math.PI * 2)
    c.fillStyle = 'rgba(24, 20, 16, 0.92)'; c.fill()
    c.lineWidth = 1.5; c.strokeStyle = ring; c.stroke()
    c.fillStyle = ring
    c.font = `700 ${Math.round(size * 1.35)}px "Noto Sans CJK SC","PingFang SC","Hiragino Sans GB",sans-serif`
    c.textAlign = 'center'; c.textBaseline = 'middle'
    c.fillText(GLYPH[kind] ?? '·', sx, sy + 0.5)
  }

  function arrow(c, sx, sy, ang, size = 7) {
    c.save(); c.translate(sx, sy); c.rotate(ang)
    c.beginPath(); c.moveTo(size, 0); c.lineTo(-size * 0.75, size * 0.7); c.lineTo(-size * 0.35, 0); c.lineTo(-size * 0.75, -size * 0.7); c.closePath()
    c.fillStyle = '#ffe7a3'; c.strokeStyle = '#1a140c'; c.lineWidth = 1.6; c.stroke(); c.fill()
    c.restore()
  }

  function drawMini() {
    const w = ctx._w
    const h = ctx._h
    ctx.clearRect(0, 0, w, h)
    // world "up on screen" direction → rotation that puts it at the top of the map
    const c1 = Math.cos(state.az)
    const s1 = Math.sin(state.az)
    const ux = -1 * c1 + -1 * s1          // rotateY(-1,-1, az) (same maths as the hero controller)
    const uz = 1 * s1 + -1 * c1
    const theta = Math.atan2(-ux, -uz)
    const s = MINI_PX_PER_M / meta.ppm
    ctx.save()
    ctx.beginPath(); ctx.rect(0, 0, w, h); ctx.clip()
    ctx.translate(w / 2, h / 2); ctx.rotate(theta); ctx.scale(s, s)
    const [hpx, hpz] = mapPx(state.hx, state.hz)
    ctx.translate(-hpx, -hpz)
    ctx.imageSmoothingEnabled = true
    ctx.drawImage(img, 0, 0)
    ctx.drawImage(fog, 0, 0, img.width, img.height)
    ctx.restore()
    const toScreen = (x, z) => {
      const dx = (x - state.hx) * MINI_PX_PER_M
      const dz = (z - state.hz) * MINI_PX_PER_M
      return [w / 2 + dx * Math.cos(theta) - dz * Math.sin(theta), h / 2 + dx * Math.sin(theta) + dz * Math.cos(theta)]
    }
    // landmarks you've discovered
    for (const p of data.pois) {
      if (!discovered.has(p.id)) continue
      const [sx, sy] = toScreen(p.x, p.z)
      if (sx < -10 || sy < -10 || sx > w + 10 || sy > h + 10) continue
      drawIcon(ctx, sx, sy, p.kind, 7)
    }
    // townsfolk nearby (real positions, shown only where the fog has cleared)
    for (const n of state.npcs) {
      const gx = Math.floor((n.x - meta.x0) / CELL_M)
      const gz = Math.floor((n.z - meta.z0) / CELL_M)
      if (!grid[gz * gw + gx]) continue
      const [sx, sy] = toScreen(n.x, n.z)
      if (sx < 0 || sy < 0 || sx > w || sy > h) continue
      ctx.beginPath(); ctx.arc(sx, sy, 3, 0, Math.PI * 2)
      ctx.fillStyle = n.kind === 'thief' ? '#9b6bff' : '#c9a24a'; ctx.fill()
      ctx.lineWidth = 1; ctx.strokeStyle = '#140f0a'; ctx.stroke()
    }
    // hero arrow (heading rotated into the map's frame)
    const hx = Math.sin(state.heading)
    const hz = Math.cos(state.heading)
    arrow(ctx, w / 2, h / 2, Math.atan2(hx * Math.sin(theta) + hz * Math.cos(theta), hx * Math.cos(theta) - hz * Math.sin(theta)))
    // compass N
    const [nx, ny] = [w - 18, 18]
    const na = -theta
    ctx.save(); ctx.translate(nx, ny); ctx.rotate(na)
    ctx.beginPath(); ctx.arc(0, 0, 11, 0, Math.PI * 2); ctx.fillStyle = 'rgba(20,18,14,0.78)'; ctx.fill()
    ctx.fillStyle = '#ff7a66'; ctx.beginPath(); ctx.moveTo(0, -9); ctx.lineTo(3.2, 1); ctx.lineTo(-3.2, 1); ctx.closePath(); ctx.fill()
    ctx.fillStyle = '#f0e2b8'; ctx.font = '700 7px sans-serif'; ctx.textAlign = 'center'; ctx.fillText('N', 0, 8.5)
    ctx.restore()
  }

  function drawBig() {
    const w = bctx._w
    const h = bctx._h
    bctx.clearRect(0, 0, w, h)
    bctx.drawImage(img, 0, 0, w, h)
    bctx.drawImage(fog, 0, 0, w, h)
    const k = w / meta.w // px per metre
    const at = (x, z) => [(x - meta.x0) * k, (z - meta.z0) * k]
    bctx.font = '700 11px "Barlow Condensed", sans-serif'
    for (const p of data.pois) {
      if (!discovered.has(p.id)) continue
      const [sx, sy] = at(p.x, p.z)
      drawIcon(bctx, sx, sy, p.kind, 9)
      bctx.textAlign = 'center'; bctx.textBaseline = 'top'
      bctx.lineWidth = 3; bctx.strokeStyle = 'rgba(14,12,10,0.9)'; bctx.strokeText(p.name.toUpperCase(), sx, sy + 12)
      bctx.fillStyle = '#f6ecd0'; bctx.fillText(p.name.toUpperCase(), sx, sy + 12)
    }
    const [hx, hz] = at(state.hx, state.hz)
    arrow(bctx, hx, hz, Math.PI / 2 - state.heading, 9)
  }

  function refreshChrome() {
    const near = data.pois.find((p) => discovered.has(p.id) && Math.hypot(state.hx - p.x, state.hz - p.z) <= 12)
    nearEl.textContent = near ? `Near: ${near.name}` : ''
    if (bigOpen) {
      bigEl.querySelector('[data-pct]').textContent = `EXPLORED ${explored()}%`
      bigEl.querySelector('[data-legend]').innerHTML = data.pois
        .map((p) => (discovered.has(p.id) ? `<li><b>${GLYPH[p.kind] ?? ''}</b> ${p.name} <i>${p.cn}</i></li>` : '<li class="unk"><b>?</b> Undiscovered</li>'))
        .join('')
    }
  }

  // ── API ──────────────────────────────────────────────────────────────────
  function update(s) {
    if (!visible || !img || !data) return
    state = s
    reveal(s.hx, s.hz)
    if (fogDirty) rebuildFog()
    if ((frame = (frame + 1) % 2) !== 0) return
    drawMini()
    if (bigOpen) drawBig()
    if (frame === 0) refreshChrome()
  }
  function setVisible(v) {
    visible = !!v
    root.classList.toggle('on', visible)
    if (!visible) setBig(false)
    else { lastRevealX = NaN; if (img) sizeCanvases() }
  }
  function setBig(open) {
    bigOpen = !!open && visible && !!img
    bigEl.classList.toggle('on', bigOpen)
    if (bigOpen) { sizeCanvases(); fogDirty = true; refreshChrome() }
  }

  root.querySelector('.cap-map__expand').addEventListener('click', () => setBig(true))
  cv.addEventListener('click', () => setBig(true))
  bigEl.querySelector('.cap-bigmap__x').addEventListener('click', () => setBig(false))
  bigEl.addEventListener('click', (e) => { if (e.target === bigEl) setBig(false) })
  document.addEventListener('keydown', (e) => {
    if (!visible || e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return
    if (e.key === 'm' || e.key === 'M') setBig(!bigOpen)
    else if (e.key === 'Escape' && bigOpen) setBig(false)
  })

  return {
    attach, update, setVisible,
    pois: () => data?.pois ?? [],
    restricted: () => data?.restricted ?? [],
    isDiscovered: (id) => discovered.has(id),
    explored,
    forget: () => { grid?.fill(0); discovered.clear(); fogDirty = true; save() }, // DEV
  }
}
